import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MAX_AUDIO_BASE64_CHARS,
  MAX_RECORDING_MS,
  RECORDING_BITS_PER_SECOND,
  blobToDataUrl,
  normalizeAudioMimeType,
  pickRecordingMimeType,
} from '../utils/audio';

export type VoiceRecorderPhase = 'idle' | 'starting' | 'recording' | 'encoding';

// Why a recording could not start or be used. The composer maps these to copy.
export type VoiceRecorderError =
  | 'unsupported'
  | 'permission_denied'
  | 'no_device'
  | 'too_large'
  | 'failed';

export type VoiceRecording = {
  // "data:<mime>;base64,<payload>" — what the socket carries as file_data.
  dataUrl: string;
  // Normalised MIME — what the socket carries as file_type.
  mimeType: string;
  durationMillis: number;
};

// How often the timer and the level meter refresh.
const TICK_MS = 100;
// MediaRecorder hands data over in slices; a small slice bounds what is lost
// if the tab dies mid-note and keeps the final blob assembly cheap.
const TIMESLICE_MS = 250;

type UseVoiceRecorderOptions = {
  // Called once when the recording reaches MAX_RECORDING_MS. The caller
  // decides what to do with the captured audio (the composer sends it).
  onMaxDuration?: () => void;
};

// Microphone capture for the Sissi AI composer: permission, a duration cap, a
// level for the meter, and the base64 data URL the socket sends. Web port of
// the app's app/hooks/useVoiceRecorder.ts on top of MediaRecorder.
export function useVoiceRecorder({ onMaxDuration }: UseVoiceRecorderOptions = {}) {
  const [phase, setPhase] = useState<VoiceRecorderPhase>('idle');
  const [error, setError] = useState<VoiceRecorderError | null>(null);
  const [durationMillis, setDurationMillis] = useState(0);
  // 0 (silence) to 1 (loud), from an AnalyserNode when the browser has one.
  const [level, setLevel] = useState(0);
  const [reachedMaxDuration, setReachedMaxDuration] = useState(false);

  const phaseRef = useRef<VoiceRecorderPhase>('idle');
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const mimeTypeRef = useRef<string>('');
  // Bumped by cancel and unmount so a stopAndEncode still awaiting the
  // recorder or the encoder knows its note was discarded under it.
  const generationRef = useRef(0);
  // Read through a ref so a caller that rebuilds its handler each render does
  // not restart anything.
  const onMaxDurationRef = useRef(onMaxDuration);
  useEffect(() => {
    onMaxDurationRef.current = onMaxDuration;
  }, [onMaxDuration]);

  const setPhaseSafely = useCallback((next: VoiceRecorderPhase) => {
    phaseRef.current = next;
    setPhase(next);
  }, []);

  // Releases the microphone and the meter. Safe to call twice.
  const releaseMedia = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    analyserRef.current = null;
    audioContextRef.current?.close().catch(() => {});
    audioContextRef.current = null;
    recorderRef.current = null;
  }, []);

  const readLevel = (): number => {
    const analyser = analyserRef.current;
    if (!analyser) return 0;
    const samples = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(samples);
    let sum = 0;
    for (let i = 0; i < samples.length; i += 1) {
      const centred = (samples[i] - 128) / 128;
      sum += centred * centred;
    }
    const rms = Math.sqrt(sum / samples.length);
    // Speech at a normal distance sits around 0.05–0.3 RMS; scale so it fills
    // the meter without clipping at a shout.
    return Math.min(1, rms * 4);
  };

  const start = useCallback(async (): Promise<boolean> => {
    if (phaseRef.current !== 'idle') return false;
    setError(null);
    setReachedMaxDuration(false);
    setDurationMillis(0);
    setLevel(0);

    const mimeType = pickRecordingMimeType();
    if (!mimeType || typeof navigator.mediaDevices?.getUserMedia !== 'function') {
      setError('unsupported');
      return false;
    }
    setPhaseSafely('starting');

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const name = (err as { name?: string })?.name;
      setError(
        name === 'NotAllowedError' || name === 'SecurityError'
          ? 'permission_denied'
          : name === 'NotFoundError' || name === 'OverconstrainedError'
            ? 'no_device'
            : 'failed',
      );
      setPhaseSafely('idle');
      return false;
    }
    // The composer unmounted or cancelled while the permission prompt was up
    // (the ref moved under the await, which TypeScript cannot see).
    if ((phaseRef.current as VoiceRecorderPhase) !== 'starting') {
      stream.getTracks().forEach((track) => track.stop());
      return false;
    }

    try {
      const recorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: RECORDING_BITS_PER_SECOND,
      });
      chunksRef.current = [];
      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
      };
      // The recording dying under us (encoder error, headset unplugged,
      // permission revoked) must not leave the bar showing "Grabando…".
      const abort = (reason: string) => {
        if (phaseRef.current !== 'recording') return;
        console.warn('useVoiceRecorder: recording ended unexpectedly:', reason);
        generationRef.current += 1;
        if (recorder.state !== 'inactive') {
          recorder.onstop = null;
          try {
            recorder.stop();
          } catch {
            // Already stopped.
          }
        }
        chunksRef.current = [];
        releaseMedia();
        setError('failed');
        setPhaseSafely('idle');
      };
      recorder.onerror = () => abort('recorder error');
      stream.getAudioTracks().forEach((track) => {
        track.onended = () => abort('track ended');
      });
      recorderRef.current = recorder;
      streamRef.current = stream;
      mimeTypeRef.current = mimeType;

      // Level meter. jsdom and some embedded browsers have no AudioContext;
      // the meter then stays flat and nothing else changes.
      if (typeof AudioContext !== 'undefined') {
        try {
          const context = new AudioContext();
          const analyser = context.createAnalyser();
          analyser.fftSize = 256;
          context.createMediaStreamSource(stream).connect(analyser);
          audioContextRef.current = context;
          analyserRef.current = analyser;
        } catch (err) {
          console.warn('useVoiceRecorder: level meter unavailable', err);
        }
      }

      recorder.start(TIMESLICE_MS);
      startedAtRef.current = Date.now();
      setPhaseSafely('recording');
      tickRef.current = setInterval(() => {
        const elapsed = Date.now() - startedAtRef.current;
        setDurationMillis(elapsed);
        setLevel(readLevel());
        if (elapsed >= MAX_RECORDING_MS && phaseRef.current === 'recording') {
          setReachedMaxDuration(true);
          if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
          }
          onMaxDurationRef.current?.();
        }
      }, TICK_MS);
      return true;
    } catch (err) {
      console.error('useVoiceRecorder: failed to start', err);
      stream.getTracks().forEach((track) => track.stop());
      releaseMedia();
      setError('failed');
      setPhaseSafely('idle');
      return false;
    }
  }, [releaseMedia, setPhaseSafely]);

  // Stops the recorder and resolves once its last chunk has landed.
  const stopRecorder = useCallback((): Promise<Blob> => {
    const recorder = recorderRef.current;
    const mimeType = mimeTypeRef.current;
    return new Promise((resolve) => {
      if (!recorder || recorder.state === 'inactive') {
        resolve(new Blob(chunksRef.current, { type: mimeType }));
        return;
      }
      recorder.onstop = () => resolve(new Blob(chunksRef.current, { type: mimeType }));
      try {
        recorder.stop();
      } catch (err) {
        console.warn('useVoiceRecorder: stop threw', err);
        resolve(new Blob(chunksRef.current, { type: mimeType }));
      }
    });
  }, []);

  // Stops and encodes. Returns null when nothing usable was captured; `error`
  // then says why (or stays null for a plain empty capture).
  const stopAndEncode = useCallback(async (): Promise<VoiceRecording | null> => {
    if (phaseRef.current !== 'recording') return null;
    setPhaseSafely('encoding');
    const generation = generationRef.current;
    const capturedDuration = Date.now() - startedAtRef.current;
    const blob = await stopRecorder();
    // Cancelled while the recorder was finishing: nothing to send, no error.
    if (generationRef.current !== generation) return null;
    releaseMedia();

    if (blob.size === 0) {
      setError('failed');
      setPhaseSafely('idle');
      return null;
    }
    const mimeType = normalizeAudioMimeType(mimeTypeRef.current);
    let dataUrl: string;
    try {
      dataUrl = await blobToDataUrl(blob, mimeType);
    } catch (err) {
      if (generationRef.current !== generation) return null;
      console.error('useVoiceRecorder: failed to encode', err);
      setError('failed');
      setPhaseSafely('idle');
      return null;
    }
    // Cancelled while encoding: the note was discarded, do not hand it back.
    if (generationRef.current !== generation) return null;
    setPhaseSafely('idle');
    // The backend rejects the whole frame above this without starting a run.
    if (dataUrl.length > MAX_AUDIO_BASE64_CHARS) {
      setError('too_large');
      return null;
    }
    return { dataUrl, mimeType, durationMillis: capturedDuration };
  }, [releaseMedia, stopRecorder, setPhaseSafely]);

  // Throws the recording away and releases the microphone. Returns whether
  // there was anything to discard. Works during encoding too: the pending
  // stopAndEncode sees the generation change and returns null.
  const cancel = useCallback((): boolean => {
    if (phaseRef.current === 'idle') return false;
    generationRef.current += 1;
    const recorder = recorderRef.current;
    // While encoding, stopAndEncode has already asked the recorder to stop and
    // is waiting for onstop; leave that in place so its promise settles (it
    // then sees the generation change and drops the note).
    if (recorder && phaseRef.current === 'recording' && recorder.state !== 'inactive') {
      recorder.onstop = null;
      try {
        recorder.stop();
      } catch {
        // Already stopped.
      }
    }
    chunksRef.current = [];
    releaseMedia();
    setDurationMillis(0);
    setLevel(0);
    setReachedMaxDuration(false);
    setPhaseSafely('idle');
    return true;
  }, [releaseMedia, setPhaseSafely]);

  // Unmounting mid-recording (closing the panel, switching conversation) must
  // release the microphone: the browser keeps the "recording" indicator on
  // for as long as a track is live.
  useEffect(
    () => () => {
      phaseRef.current = 'idle';
      generationRef.current += 1;
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.onstop = null;
        try {
          recorder.stop();
        } catch {
          // Already stopped.
        }
      }
      releaseMedia();
    },
    [releaseMedia],
  );

  return {
    phase,
    isRecording: phase === 'recording',
    isEncoding: phase === 'encoding',
    error,
    clearError: () => setError(null),
    durationMillis,
    level,
    reachedMaxDuration,
    start,
    stopAndEncode,
    cancel,
  };
}
